import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  FileText,
  Calculator,
  PieChart,
  FileCheck,
  Building2,
  TrendingUp,
  BookOpen,
  Scale,
  Search,
  ArrowRight,
} from "lucide-react";

const ServiziPage = () => {
  const services = [
    {
      icon: FileText,
      title: "Fatturazione Elettronica e Digitalizzazione Aziendale",
      description:
        "Supportiamo le aziende nel passaggio alla fatturazione elettronica e nella digitalizzazione dei processi contabili. Offriamo consulenza completa per l'implementazione di sistemi digitali efficienti e conformi alla normativa.",
      features: [
        "Implementazione sistema di fatturazione elettronica",
        "Conservazione digitale a norma",
        "Formazione del personale",
        "Assistenza tecnica continua",
      ],
    },
    {
      icon: Search,
      title: "Ricerca e Assistenza per Bandi di Contributo",
      description:
        "Monitoriamo costantemente i bandi regionali, nazionali ed europei per individuare le opportunità di finanziamento più adatte alla tua attività. Ti assistiamo nella preparazione e presentazione delle domande.",
      features: [
        "Monitoraggio bandi attivi",
        "Valutazione requisiti",
        "Preparazione documentazione",
        "Assistenza nella rendicontazione",
      ],
    },
    {
      icon: PieChart,
      title: "Controllo di Gestione",
      description:
        "Analizziamo e monitoriamo le performance aziendali per supportare le decisioni strategiche. Implementiamo sistemi di controllo che permettono di tenere sotto controllo costi, ricavi e margini.",
      features: [
        "Analisi dei costi e dei ricavi",
        "Budget e forecast",
        "Reportistica periodica",
        "Indicatori di performance (KPI)",
      ],
    },
    {
      icon: FileCheck,
      title: "Dichiarazioni Fiscali",
      description:
        "Gestiamo tutte le tipologie di dichiarazioni fiscali per privati e aziende, garantendo precisione, puntualità e ottimizzazione del carico fiscale nel rispetto della normativa vigente.",
      features: [
        "Modello 730 e Modello Redditi",
        "Dichiarazione IVA",
        "Modello 770",
        "Comunicazioni periodiche",
      ],
    },
    {
      icon: Building2,
      title: "Consulenza per Enti Non Commerciali",
      description:
        "Offriamo consulenza specializzata per associazioni, enti del terzo settore e organizzazioni non profit. Supportiamo nella gestione contabile, fiscale e negli adempimenti specifici del settore.",
      features: [
        "Costituzione e statuti",
        "Contabilità associativa",
        "Adempimenti fiscali specifici",
        "Riforma del Terzo Settore",
      ],
    },
    {
      icon: TrendingUp,
      title: "Pianificazione Fiscale",
      description:
        "Sviluppiamo strategie di pianificazione fiscale personalizzate per ottimizzare il carico tributario della tua attività, sempre nel pieno rispetto delle normative e con un approccio proattivo.",
      features: [
        "Analisi della situazione fiscale",
        "Strategie di ottimizzazione",
        "Scelta del regime fiscale",
        "Pianificazione investimenti",
      ],
    },
    {
      icon: BookOpen,
      title: "Contabilità e Analisi di Bilancio",
      description:
        "Gestiamo la contabilità ordinaria e semplificata per imprese di ogni dimensione. Elaboriamo bilanci e analisi economico-finanziarie per supportare le decisioni aziendali.",
      features: [
        "Contabilità ordinaria e semplificata",
        "Elaborazione bilanci",
        "Analisi indici di bilancio",
        "Reportistica personalizzata",
      ],
    },
    {
      icon: Calculator,
      title: "Consulenza Fiscale",
      description:
        "Forniamo assistenza qualificata e aggiornata in tutti gli ambiti fiscali. Supportiamo imprese, privati e liberi professionisti nella corretta interpretazione e applicazione delle norme tributarie.",
      features: [
        "Apertura Partita IVA",
        "Scelta regime fiscale",
        "Consulenza IVA e imposte dirette",
        "Assistenza in caso di accertamenti",
      ],
    },
    {
      icon: Scale,
      title: "Consulenza Tributaria",
      description:
        "Offriamo consulenza specializzata in materia tributaria, assistendo i clienti nella gestione dei rapporti con l'Amministrazione finanziaria e nella risoluzione di problematiche complesse.",
      features: [
        "Interpretazione normativa",
        "Contenzioso tributario",
        "Istanze di interpello",
        "Ravvedimento operoso",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-brand-background" data-testid="servizi-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-brand-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-white/60 text-sm font-medium tracking-wider uppercase">
              I Nostri Servizi
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mt-4 mb-6">
              Soluzioni complete per la tua attività
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Offriamo una gamma completa di servizi di consulenza fiscale,
              tributaria e contabile per imprese, privati e liberi professionisti.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid gap-8">
            {services.map((service, index) => (
              <Card
                key={service.title}
                className="service-card border-brand-border overflow-hidden"
                data-testid={`service-detail-${index}`}
              >
                <div className="grid md:grid-cols-3 gap-0">
                  <div className="md:col-span-2 p-8 md:p-10">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 bg-brand-secondary rounded-xl flex items-center justify-center">
                        <service.icon className="text-brand-primary" size={28} />
                      </div>
                      <h2 className="font-heading text-2xl font-semibold text-brand-foreground">
                        {service.title}
                      </h2>
                    </div>
                    <p className="text-brand-muted leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <Link to="/contatti">
                      <Button
                        variant="outline"
                        className="border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white rounded-full"
                        data-testid={`service-cta-${index}`}
                      >
                        Richiedi Info
                        <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  </div>
                  <div className="bg-brand-secondary p-8 md:p-10">
                    <h3 className="font-heading text-lg font-semibold text-brand-foreground mb-4">
                      Cosa include
                    </h3>
                    <ul className="space-y-3">
                      {service.features.map((feature, fIndex) => (
                        <li
                          key={fIndex}
                          className="flex items-start gap-3 text-brand-muted"
                        >
                          <span className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-secondary">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-brand-foreground mb-6">
            Non trovi quello che cerchi?
          </h2>
          <p className="text-brand-muted text-lg mb-8">
            Contattaci per una consulenza personalizzata. Troveremo insieme la
            soluzione più adatta alle tue esigenze.
          </p>
          <Link to="/contatti">
            <Button
              size="lg"
              className="bg-brand-primary hover:bg-brand-primary-dark text-white rounded-full px-10"
              data-testid="servizi-final-cta"
            >
              Contattaci
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiziPage;
